import { cn} from '@/lib/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search,
  Loader2,
  Briefcase,
  CalendarPlus,
  CheckCircle2,
  Hammer,
  TrendingDown,
  Percent,
  ClipboardList,
  Coins,
  TrendingUp,
  Wallet,
  Calendar,
  AlertCircle,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import StatusBadge from "@/components/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import { JOB_STATUS_LABELS, jobStatusTone } from "@/lib/constants";
import { formatDate, formatCurrency } from "@/lib/format";
import { marginTone } from "@/lib/profit";
import { useAuth } from "@/contexts/AuthContext";

const DELAY_LABELS = { montaj: "Montaj Gecikmiş", siparis: "Sipariş Gecikmiş" };

export default function DashboardPage() {
  const navigate = useNavigate();
  const { settings } = useAuth();
  const minMargin = settings?.min_margin_percent ?? 20;

  const [kpis, setKpis] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [delayed, setDelayed] = useState({ items: [], total: 0 });
  const [installmentReminders, setInstallmentReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [k, u, d, i] = await Promise.all([
        api.get("/dashboard/kpis"),
        api.get("/dashboard/upcoming-installations"),
        api.get("/dashboard/delayed-jobs"),
        api.get("/dashboard/installments-reminders").catch(() => ({ data: [] })),
      ]);
      setKpis(k.data);
      setUpcoming(u.data.items);
      setDelayed({ items: d.data.items, total: d.data.total });
      setInstallmentReminders(i.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const marginBadge = useMemo(() => {
    if (!kpis?.avg_gross_margin) return null;
    return marginTone(kpis.avg_gross_margin, minMargin);
  }, [kpis, minMargin]);

  const weightedMarginToneObj = useMemo(() => {
    if (kpis?.weighted_gross_margin == null) return null;
    return marginTone(kpis.weighted_gross_margin, minMargin);
  }, [kpis, minMargin]);

  const navigateToMonthNewJobs = () => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const lastDay = new Date(yyyy, now.getMonth() + 1, 0).getDate();
    const startStr = `${yyyy}-${mm}-01T00:00:00+03:00`;
    const endStr = `${yyyy}-${mm}-${String(lastDay).padStart(2, "0")}T23:59:59+03:00`;
    navigate(`/isler?exclude_teklif=true&contract_date_from=${encodeURIComponent(startStr)}&contract_date_to=${encodeURIComponent(endStr)}`);
  };

  const navigateToMonthCompletedJobs = () => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const lastDay = new Date(yyyy, now.getMonth() + 1, 0).getDate();
    const startStr = `${yyyy}-${mm}-01T00:00:00+03:00`;
    const endStr = `${yyyy}-${mm}-${String(lastDay).padStart(2, "0")}T23:59:59+03:00`;
    navigate(`/isler?status=is_kapatildi&closing_date_from=${encodeURIComponent(startStr)}&closing_date_to=${encodeURIComponent(endStr)}`);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 bg-[#F4F6F8] min-h-full" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#171A1F] tracking-tight">Yönetici Panosu</h1>
          <p className="text-xs text-slate-500 mt-1">Şirket operasyonunun 20 saniyelik özeti.</p>
        </div>
        <QuickSearch />
      </div>

      {/* KPI cards */}
      {loading || !kpis ? (
        <div className="flex items-center justify-center gap-2 py-16 text-slate-500" data-testid="dashboard-loading">
          <Loader2 className="animate-spin text-[#FF353A]" size={18} /> Yükleniyor...
        </div>
      ) : (
        <>
          <div className="space-y-6">
            {/* Operasyon */}
            <div className="space-y-2">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Operasyon</h2>
              <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                <KpiCard testId="kpi-offers-given" icon={ClipboardList} label="Verilen Teklifler" value={kpis.offers_given} sub="Teklif aşamasında" iconBg="red" onClick={() => navigate("/isler?status=teklif_verildi")} />
                <KpiCard testId="kpi-open-jobs" icon={Briefcase} label="Açık İş" value={kpis.open_jobs} sub="Kapatılmamış" iconBg="blue" onClick={() => navigate("/isler?status=open")} />
                <KpiCard testId="kpi-active-orders" icon={ClipboardList} label="Aktif Sipariş" value={kpis.active_orders} sub="Devam eden" iconBg="blue" onClick={() => navigate("/siparisler")} />
                <KpiCard testId="kpi-month-new" icon={CalendarPlus} label="Bu Ay Alınan" value={kpis.month_new_jobs} sub="Yeni iş kaydı" iconBg="red" onClick={navigateToMonthNewJobs} />
                <KpiCard testId="kpi-month-completed" icon={CheckCircle2} label="Bu Ay Tamamlanan" value={kpis.month_completed_jobs} sub="Kapatılan iş" iconBg="blue" onClick={navigateToMonthCompletedJobs} />
              </div>
            </div>

            {/* Finans */}
            <div className="space-y-2">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Finans</h2>
              <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                <KpiCard testId="kpi-month-sales" icon={Coins} label="Bu Ayki Satış" value={formatCurrency(kpis.this_month_sales)} sub="Yeni alınan işler" iconBg="red" />
                <KpiCard testId="kpi-month-costs" icon={TrendingDown} label="Bu Ayki Maliyet" value={formatCurrency(kpis.this_month_costs)} sub="Gerçekleşen maliyetler" iconBg="blue" />
                <KpiCard testId="kpi-month-profit" icon={TrendingUp} label="Bu Ayki Brüt Kâr" value={formatCurrency(kpis.this_month_gross_profit)} sub={parseFloat(kpis.this_month_gross_profit) >= 0 ? "Kâr" : "Zarar"} valueClass={parseFloat(kpis.this_month_gross_profit) >= 0 ? "text-emerald-700" : "text-rose-700"} tone={parseFloat(kpis.this_month_gross_profit) >= 0 ? "success" : "danger"} />
                <KpiCard testId="kpi-weighted-margin" icon={Percent} label="Brüt Marj" value={kpis.weighted_gross_margin != null ? `%${kpis.weighted_gross_margin}` : "-"} sub={weightedMarginToneObj?.label || "Brüt Marj"} valueClass={weightedMarginToneObj?.className} tone={weightedMarginToneObj?.tone || "neutral"} />
                <KpiCard testId="kpi-month-collection" icon={Wallet} label="Bu Ay Tahsilat" value={formatCurrency(kpis.this_month_collection)} tone="success" iconBg="blue" onClick={() => navigate("/tahsilatlar")} />
                <KpiCard testId="kpi-collection-ratio" icon={TrendingUp} label="Genel Tahsilat Oranı" value={`%${parseFloat(kpis.collection_percentage || 0).toFixed(1)}`} sub="Toplam tahsilat / toplam satış" tone="neutral" iconBg="blue" onClick={() => navigate("/tahsilatlar")} />
                <KpiCard testId="kpi-open-receivable" icon={Calendar} label="Açık Alacak" value={formatCurrency(kpis.open_receivable)} tone="warning" iconBg="red" onClick={() => navigate("/raporlar/alacaklar?mode=open")} />
                <KpiCard testId="kpi-overdue-receivable" icon={AlertCircle} label="Geciken Alacak" value={formatCurrency(kpis.overdue_receivable)} tone="danger" iconBg="red" onClick={() => navigate("/raporlar/alacaklar?mode=overdue")} />
              </div>
            </div>
          </div>

          <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
            {/* Upcoming installations */}
            <div className="rounded-lg border border-[#DCE2E8] bg-white shadow-sm flex flex-col overflow-hidden">
              <div className="px-4 py-3 border-b border-[#DCE2E8] flex items-center justify-between">
                <h2 className="text-sm font-semibold text-[#171A1F]">Bu Hafta Planlanan Montajlar</h2>
                <span className="text-xs text-slate-500 font-medium">{upcoming.length} kayıt</span>
              </div>
              {upcoming.length === 0 ? (
                <div className="p-6 text-sm text-slate-500 text-center" data-testid="upcoming-empty">Bu hafta planlanmış montaj yok.</div>
              ) : (
                <>
                  {/* Mobile Card List */}
                  <div className="block md:hidden divide-y divide-slate-100">
                    {upcoming.map((it) => (
                      <div
                        key={it.job_id}
                        onClick={() => navigate(`/isler/${it.job_id}`)}
                        className="p-3.5 space-y-2 cursor-pointer hover:bg-[#EAF7FF]/30 active:bg-[#EAF7FF]/60 transition-colors"
                        data-testid={`upcoming-mobile-card-${it.job_number}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-xs font-bold text-[#FF353A]">{it.job_number}</span>
                          <StatusBadge tone={jobStatusTone(it.status)}>{JOB_STATUS_LABELS[it.status]}</StatusBadge>
                        </div>
                        <div className="text-sm font-semibold text-[#171A1F]">{it.customer_name}</div>
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span>{it.district || "-"}</span>
                          <span>{formatDate(it.planned_date)}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop Table View */}
                  <Table className="hidden md:table">
                    <TableHeader>
                      <TableRow className="bg-[#F4F6F8] hover:bg-[#F4F6F8]">
                        <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">İş No</TableHead>
                        <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Müşteri</TableHead>
                        <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">İlçe</TableHead>
                        <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Tarih</TableHead>
                        <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Durum</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {upcoming.map((it) => (
                        <TableRow key={it.job_id} onClick={() => navigate(`/isler/${it.job_id}`)} className="gp-row cursor-pointer" data-testid={`upcoming-row-${it.job_number}`}>
                          <TableCell className="font-mono text-xs font-semibold text-[#FF353A]">{it.job_number}</TableCell>
                          <TableCell className="text-sm text-[#171A1F]">{it.customer_name}</TableCell>
                          <TableCell className="text-sm text-slate-600">{it.district || "-"}</TableCell>
                          <TableCell className="text-sm text-slate-600">{formatDate(it.planned_date)}</TableCell>
                          <TableCell><StatusBadge tone={jobStatusTone(it.status)}>{JOB_STATUS_LABELS[it.status]}</StatusBadge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </div>

            {/* Delayed jobs */}
            <div className="rounded-lg border border-[#DCE2E8] bg-white shadow-sm flex flex-col overflow-hidden">
              <div className="px-4 py-3 border-b border-[#DCE2E8] flex items-center justify-between">
                <h2 className="text-sm font-semibold text-[#171A1F]">Geciken İşler</h2>
                <span className="text-xs text-slate-500 font-medium">Toplam {delayed.total}</span>
              </div>
              {delayed.items.length === 0 ? (
                <div className="p-6 text-sm text-slate-500 text-center" data-testid="delayed-empty">Şu anda geciken iş yok.</div>
              ) : (
                <>
                  {/* Mobile Card List */}
                  <div className="block md:hidden divide-y divide-slate-100">
                    {delayed.items.map((it) => (
                      <div
                        key={it.job_id}
                        onClick={() => navigate(`/isler/${it.job_id}`)}
                        className="p-3.5 space-y-2 cursor-pointer hover:bg-rose-50/30 active:bg-rose-50/60 transition-colors"
                        data-testid={`delayed-mobile-card-${it.job_number}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-xs font-bold text-[#FF353A]">{it.job_number}</span>
                          <span className="font-mono text-xs font-bold text-rose-700">{it.delay_days} gün gecikme</span>
                        </div>
                        <div className="text-sm font-semibold text-[#171A1F]">{it.customer_name}</div>
                        <div className="flex flex-wrap gap-1 pt-0.5">
                          {it.delay_types.map((t) => (
                            <span key={t} className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-rose-100 text-rose-800 border border-rose-200">{DELAY_LABELS[t]}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop Table View */}
                  <Table className="hidden md:table">
                    <TableHeader>
                      <TableRow className="bg-[#F4F6F8] hover:bg-[#F4F6F8]">
                        <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">İş No</TableHead>
                        <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Müşteri</TableHead>
                        <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Tür</TableHead>
                        <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 text-right">Gün</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {delayed.items.map((it) => (
                        <TableRow key={it.job_id} onClick={() => navigate(`/isler/${it.job_id}`)} className="gp-row cursor-pointer" data-testid={`delayed-row-${it.job_number}`}>
                          <TableCell className="font-mono text-xs font-semibold text-[#FF353A]">{it.job_number}</TableCell>
                          <TableCell className="text-sm text-[#171A1F]">{it.customer_name}</TableCell>
                          <TableCell className="text-xs">
                            <div className="flex flex-wrap gap-1">
                              {it.delay_types.map((t) => (
                                <span key={t} className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-rose-100 text-rose-800 border border-rose-200">{DELAY_LABELS[t]}</span>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm font-bold text-rose-700">{it.delay_days}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </div>


          <div className="rounded-lg border border-[#DCE2E8] bg-white shadow-sm flex flex-col overflow-hidden">
            <div className="px-4 py-3 border-b border-[#DCE2E8] flex items-center justify-between bg-slate-50/50">
              <h2 className="text-sm font-semibold text-[#171A1F] flex items-center gap-2">
                <AlertCircle size={16} className="text-[#FF353A]" /> Yaklaşan / Geciken Tahsilatlar
              </h2>
            </div>
            {installmentReminders.length === 0 ? (
              <div className="p-6 text-sm text-slate-500 text-center">Yaklaşan veya geciken tahsilat bulunmuyor.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {installmentReminders.map(r => {
                  const time = new Date(r.due_date);
                  const timeStr = `${String(time.getDate()).padStart(2, "0")}.${String(time.getMonth() + 1).padStart(2, "0")}.${time.getFullYear()}`;
                  const isGecikti = r.status === 'Gecikti';
                  return (
                    <li
                      key={r.id}
                      onClick={() => navigate(`/isler/${r.job_id}`)}
                      className={`px-4 py-3 flex items-center gap-3 text-sm min-h-[44px] cursor-pointer hover:bg-[#EAF7FF]/40 active:bg-[#EAF7FF]/70`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[#171A1F] font-medium truncate">{r.customer_name}</span>
                          <span className={`font-mono text-xs font-semibold shrink-0 ${isGecikti ? 'text-gp-red' : 'text-orange-600'}`}>{r.status}</span>
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          Vade: {timeStr}
                        </div>
                      </div>
                      <span className="font-mono text-sm font-bold text-gp-blue shrink-0">{formatCurrency(r.remaining_amount)}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </>
      )}
    </div>
  );
}

function getKpiValueSizeClass(val) {
  const str = val != null ? String(val).trim() : "";
  const len = str.length;

  if (len > 18) {
    // 19+ chars (e.g. "1.123.456.789,00 TL")
    return "text-xs min-[400px]:text-sm sm:text-sm lg:text-xs xl:text-sm";
  }
  if (len > 15) {
    // 16-18 chars (e.g. "39.047.470,00 TL" (16), "123.456.789,00 TL" (17))
    return "text-sm min-[400px]:text-base sm:text-base lg:text-sm xl:text-base";
  }
  if (len > 12) {
    // 13-15 chars (e.g. "6.581.379,32 TL" (15))
    return "text-sm min-[400px]:text-base sm:text-base lg:text-base xl:text-lg";
  }
  if (len > 6) {
    // 7-12 chars (e.g. "15.000,00 TL" (12))
    return "text-base sm:text-lg xl:text-xl";
  }
  // Short numbers, percentages (e.g. "12", "%24.5")
  return "text-xl sm:text-2xl";
}

function KpiCard({ icon: Icon, label, value, sub, tone = "neutral", iconBg = "red", valueClass, testId, onClick }) {
  const cardStyle = {
    neutral: "border-[#DCE2E8] bg-white",
    danger: "border-rose-200 bg-rose-50/40",
    warning: "border-amber-200 bg-amber-50/40",
    success: "border-emerald-200 bg-emerald-50/40",
  }[tone];

  const iconContainerClass = {
    red: "bg-[#FFF0F0] text-[#FF353A]",
    blue: "bg-[#EAF7FF] text-[#168DEB]",
  }[iconBg] || "bg-[#FFF0F0] text-[#FF353A]";

  const iconTone = {
    neutral: iconContainerClass,
    danger: "bg-rose-100 text-rose-700",
    warning: "bg-amber-100 text-amber-700",
    success: "bg-emerald-100 text-emerald-700",
  }[tone];

  const sizeClass = getKpiValueSizeClass(value);

  return (
    <div
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (onClick && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
      className={`rounded-lg border ${cardStyle} p-3.5 select-none shadow-sm min-h-[90px] flex flex-col justify-between ${
        onClick
          ? "cursor-pointer hover:shadow hover:border-slate-300 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-[#FF353A] transition-all"
          : ""
      }`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 truncate min-w-0">{label}</span>
        <div className={`p-1.5 rounded-md shrink-0 flex items-center justify-center ${iconTone}`}>
          <Icon size={14} />
        </div>
      </div>
      <div className="min-w-0">
        <div
          className={`mt-1 font-mono ${sizeClass} font-bold tracking-tight whitespace-nowrap ${valueClass || "text-[#171A1F]"}`}
          title={value != null ? String(value) : undefined}
          data-testid={`${testId}-value`}
        >
          {value ?? "-"}
        </div>
        {sub && <div className={`text-[11px] mt-0.5 truncate ${valueClass || "text-slate-500"}`} title={sub}>{sub}</div>}
      </div>
    </div>
  );
}

function QuickSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!q || q.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/dashboard/quick-search", { params: { q, limit: 10 } });
        setResults(r.data.items);
        setOpen(true);
      } catch (_) {} finally {
        setLoading(false);
      }
    }, 250);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [q]);

  const pick = (job) => {
    setQ("");
    setResults([]);
    setOpen(false);
    navigate(`/isler/${job.id}`);
  };

  return (
    <div className="relative w-full sm:max-w-md" data-testid="quick-search">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="İş no, müşteri veya telefon ara..."
        className="pl-9 border-[#DCE2E8] bg-white focus-visible:ring-[#FF353A] text-base md:text-sm h-11 sm:h-9"
        data-testid="quick-search-input"
        onFocus={() => q.length >= 2 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-[#DCE2E8] bg-white shadow-xl max-h-[60vh] overflow-auto" data-testid="quick-search-results">
          {loading && <div className="px-3.5 py-3 text-xs text-slate-500 inline-flex items-center gap-2"><Loader2 className="animate-spin text-[#FF353A]" size={14} /> Aranıyor...</div>}
          {!loading && results.length === 0 && (
            <div className="px-3.5 py-3 text-xs text-slate-500">Sonuç bulunamadı.</div>
          )}
          {results.map((r) => (
            <button
              key={r.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(r)}
              className="w-full text-left px-3.5 py-2.5 min-h-[44px] hover:bg-[#EAF7FF]/50 border-b border-[#DCE2E8] last:border-b-0 transition-colors flex flex-col justify-center"
              data-testid={`quick-search-result-${r.job_number}`}
            >
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs font-semibold text-[#FF353A]">{r.job_number}</span>
                <StatusBadge tone={jobStatusTone(r.status)}>{JOB_STATUS_LABELS[r.status]}</StatusBadge>
              </div>
              <div className="text-sm text-[#171A1F] mt-0.5 font-medium">{r.customer_name}</div>
              <div className="text-xs text-slate-500">{[r.phone, r.district].filter(Boolean).join(" · ") || "-"}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
